
const HOVER_DELAY = 80;
const ACTIVE = "is-active";

export function Tabs(root) {
  if (!root || root.__tabs) return root && root.__tabs;

  const tabs = Array.from(root.querySelectorAll(".js-tabs-tab"));
  const panels = Array.from(root.querySelectorAll(".js-tabs-panel"));
  const list = root.querySelector(".js-tabs-list");
  if (!list || tabs.length === 0 || tabs.length !== panels.length) return null;

  const trigger = (root.getAttribute("data-tabs-trigger") || "click").toLowerCase();
  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const hoverEnabled = trigger === "hover" && canHover;

  const ac = new AbortController();
  const opts = { signal: ac.signal };
  let activeIndex = -1;
  let hoverTimer = 0;

  // Wire up ARIA + IDs once.
  const baseId = root.id || `tabs-${++Tabs._uid}`;
  root.id = baseId;
  tabs.forEach((tab, i) => {
    tab.id = tab.id || `${baseId}-tab-${i}`;
    panels[i].id = panels[i].id || `${baseId}-panel-${i}`;
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-controls", panels[i].id);
    panels[i].setAttribute("role", "tabpanel");
    panels[i].setAttribute("aria-labelledby", tab.id);
    panels[i].setAttribute("tabindex", "0");
  });

  function activate(i, focus) {
    if (i < 0 || i >= tabs.length || i === activeIndex) return;
    for (let n = 0; n < tabs.length; n++) {
      const on = n === i;
      tabs[n].setAttribute("aria-selected", String(on));
      tabs[n].setAttribute("tabindex", on ? "0" : "-1");
      tabs[n].classList.toggle(ACTIVE, on);
      panels[n].hidden = !on;
      panels[n].classList.toggle(ACTIVE, on);
    }
    activeIndex = i;
    if (focus) tabs[i].focus();
    root.dispatchEvent(new CustomEvent("tabs:change", {
      bubbles: true, detail: { index: i, tab: tabs[i] }
    }));
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener("click", (e) => { e.preventDefault(); activate(i, true); }, opts);
    tab.addEventListener("focus", () => activate(i, false), opts);
    if (hoverEnabled) {
      tab.addEventListener("mouseenter", () => {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => activate(i, false), HOVER_DELAY);
      }, opts);
      tab.addEventListener("mouseleave", () => clearTimeout(hoverTimer), opts);
    }
  });

  list.addEventListener("keydown", (e) => {
    const cur = tabs.indexOf(document.activeElement);
    if (cur === -1) return;
    const last = tabs.length - 1;
    let next = null;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (cur + 1) % tabs.length; break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (cur - 1 + tabs.length) % tabs.length; break;
      case "Home": next = 0; break;
      case "End":  next = last; break;
      case "Enter":
      case " ":
        e.preventDefault(); activate(cur, true); return;
      default: return;
    }
    e.preventDefault();
    activate(next, true);
  }, opts);

  const def = parseInt(root.getAttribute("data-tabs-default"), 10);
  activate(Number.isInteger(def) && def >= 0 && def < tabs.length ? def : 0, false);

  // Signal CSS that JS has taken over — enables panel fade-in transition.
  root.setAttribute("data-tabs-ready", "");

  const controller = {
    el: root,
    activate: (i) => activate(i, false),
    get activeIndex() { return activeIndex; },
    destroy() {
      clearTimeout(hoverTimer);
      ac.abort();
      root.removeAttribute("data-tabs-ready");
      delete root.__tabs;
    },
  };
  root.__tabs = controller;
  return controller;
}
Tabs._uid = 0;
